import { Component } from '@theme/component';

/**
 * Ancrage taille : le lien « guide des tailles » de la colonne d'achat.
 *
 * Le lien n'ouvre pas sa propre fenêtre : il actionne le déclencheur du block
 * popup-link natif présent dans la fiche. Une seule source de vérité pour le
 * contenu du guide, et rien à synchroniser.
 *
 * Avec `data-lrs-into-legend`, l'élément va se poser dans la légende du
 * sélecteur de variantes, pour que l'intitulé de l'option et le lien tiennent
 * sur la même ligne. C'est un déplacement, pas une reconstruction : l'intitulé
 * reste celui qu'Horizon rend, et si ce script ne s'exécute pas, le lien reste
 * où le gabarit l'a posé, visible et fonctionnel.
 *
 * @extends {Component}
 */
class LrsSizeNote extends Component {
  #moved = false;

  connectedCallback() {
    super.connectedCallback?.();
    if (this.dataset.lrsIntoLegend === 'on') this.#moveIntoLegend();
  }

  /** @returns {HTMLElement | null} la fiche, ou le document en dernier recours */
  get #scope() {
    return this.closest('.product-details');
  }

  openSizeGuide() {
    const scope = this.#scope ?? document;
    // dialog-component expose showDialog() : on appelle l'API d'Horizon plutôt
    // que de simuler un clic sur son déclencheur.
    const popup = /** @type {HTMLElement & { showDialog?: () => void }} */ (
      scope.querySelector('.popup-link')
    );
    popup?.showDialog?.();
  }

  /**
   * Pose l'élément dans la légende du groupe de tailles.
   *
   * Le groupe visé est le dernier qui ne soit pas un coloris : sur un produit
   * à plusieurs couleurs, le sélecteur rend « Couleur » puis « Taille », et le
   * guide n'a rien à faire sur la ligne des couleurs.
   */
  #moveIntoLegend() {
    if (this.#moved) return;

    const scope = this.#scope;
    const picker = scope?.querySelector('variant-picker');
    if (!picker) return;

    const groups = Array.from(picker.querySelectorAll('fieldset.variant-option'));
    const target = groups
      .filter((group) => {
        const input = group.querySelector('input[data-option-name]');
        const name = (input?.getAttribute('data-option-name') || '').trim().toLowerCase();
        return !name.includes('couleur') && !name.includes('color');
      })
      .pop();

    const legend = target?.querySelector('legend');
    if (!legend || legend.contains(this)) return;

    // Le drapeau est posé avant le déplacement : retirer puis rattacher
    // l'élément rejoue connectedCallback, et sans lui on bouclerait.
    this.#moved = true;
    legend.appendChild(this);
  }
}

if (!customElements.get('lrs-size-note')) {
  customElements.define('lrs-size-note', LrsSizeNote);
}
